import React, { useEffect, useState } from "react";
import NewModal from "../NewModal/NewModal";
import AnimatedButton from "../AnimatedButton/AnimatedButton";
import styles from "./ForwardingConditionModal.module.css";
import {
  FORWARDING_CONDITIONS,
  CONDITIONAL_KEYS,
  CONDITION_ALL,
  getNetworkForCarrier,
} from "../../lib/callForwardingCodes";

/*
 * "When Call Forwarding?" bottom sheet — opens after the user taps Continue
 * on the carrier screen. Multi-select; the selection decides how many dial
 * codes the next screen shows.
 *
 * Selection rules:
 *   - Ticking "All Calls" ticks ALL four boxes and yields ONE code (the
 *     unconditional one we already ship).
 *   - Ticking conditions individually does NOT turn into "All Calls" — even
 *     all three. That's a deliberately different choice: one code per
 *     condition, three rows on the next screen.
 *   - Unticking any box drops "All Calls" with it; unticking "All Calls"
 *     itself clears the whole sheet.
 *   - Continue stays disabled until at least one box is ticked.
 */
export default function ForwardingConditionModal({
  isOpen,
  onClose,
  onContinue,
  initialConditions = [],
  isSaving = false,
  // Needed to warn CDMA users up front — see cdmaMerge below.
  carrier = "",
  // Receptionist name shown in the subtitle ("…send calls to Sasha.").
  agentName = "",
}) {
  const [selected, setSelected] = useState(() => new Set(initialConditions));

  // Reseed on each open so a re-entry shows the previously saved choice.
  useEffect(() => {
    if (!isOpen) return;
    setSelected(new Set((initialConditions || []).filter(Boolean)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);

      if (key === CONDITION_ALL) {
        // Ticking "All Calls" ticks every box; unticking it clears the sheet.
        if (next.has(CONDITION_ALL)) return new Set();
        return new Set([CONDITION_ALL, ...CONDITIONAL_KEYS]);
      }

      if (next.has(key)) {
        // Dropping any one condition means it's no longer "all calls".
        next.delete(key);
        next.delete(CONDITION_ALL);
        return next;
      }

      // Ticking conditions one by one NEVER turns into "All Calls" — that
      // stays an explicit choice. Three individual ticks = three codes.
      next.add(key);
      return next;
    });
  };

  const canContinue = selected.size > 0 && !isSaving;

  // CDMA networks (Verizon, Bell, Telus, Visible…) have ONE conditional
  // forwarding code — *71 covers busy, unanswered and unreachable together.
  // So ticking 2-3 of them still yields a single code, which looks like the
  // sheet ignored the choice. Say so here rather than letting the next
  // screen surprise them.
  const cdmaMerge =
    getNetworkForCarrier(carrier) === "cdma" &&
    !selected.has(CONDITION_ALL) &&
    CONDITIONAL_KEYS.filter((k) => selected.has(k)).length > 1;

  const handleContinue = () => {
    if (!canContinue) return;
    onContinue(
      FORWARDING_CONDITIONS.map((c) => c.key).filter((k) => selected.has(k)),
    );
  };

  // Header is rendered inside the body (title={null}) so the subtitle can sit
  // directly under the title, above the first divider.
  return (
    <NewModal
      isOpen={isOpen}
      onClose={onClose}
      title={null}
      height="auto"
      bodyMaxHeight="70vh"
      bodyPadding="22px 20px 20px"
    >
      <div className={styles.body}>
        <h2 className={styles.title}>When should calls forward?</h2>
        <p className={styles.subtitle}>
          Choose when your business number should send calls to{" "}
          <strong>{agentName || "your receptionist"}</strong>.
        </p>

        <ul className={styles.list}>
          {FORWARDING_CONDITIONS.map((condition) => {
            const checked = selected.has(condition.key);
            // "All Calls (Unconditional)" reads long here — the short label is
            // enough now that the subtitle explains what the sheet is for.
            const label =
              condition.key === CONDITION_ALL
                ? condition.short || condition.label
                : condition.label;
            return (
              <li key={condition.key} className={styles.row}>
                <label className={styles.rowLabel}>
                  <span className={styles.rowText}>{label}</span>
                  <input
                    type="checkbox"
                    className={styles.nativeBox}
                    checked={checked}
                    onChange={() => toggle(condition.key)}
                  />
                  <span
                    className={`${styles.box}${checked ? ` ${styles.boxChecked}` : ""}`}
                    aria-hidden="true"
                  >
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M3.5 8L6.5 11L12.5 5"
                        stroke="#fff"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>

        {/* Nudge shown only while a partial conditional set is ticked. */}
        {!selected.has(CONDITION_ALL) && selected.size > 1 && (
          cdmaMerge ? (
            <p className={styles.noteCdma}>
              {carrier} uses <strong>one setting</strong> for busy, unanswered
              and unreachable — you'll get a single code covering all of them.
            </p>
          ) : (
            <p className={styles.hint}>
              We'll show you {selected.size} codes to dial — one for each.
            </p>
          )
        )}

        <div className={styles.continueWrap}>
          <AnimatedButton
            label={isSaving ? "Saving…" : "Continue"}
            onClick={handleContinue}
            isLoading={isSaving}
            disabled={!canContinue}
            positionType="relative"
            width="100%"
            bottom="0"
          />
        </div>
      </div>
    </NewModal>
  );
}
